import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import {Stock} from '../models/stock/stock.model';
import {JobStock} from '../models/stock/jobStock.model';
import {StockService} from '../services/stock.service';
import {Stocks} from '../actions/stock.actions';
import ClearState = Auth.ClearState;
import GetStock = Stocks.GetStock;
import GetStocks = Stocks.GetStocks;
import CreateStock = Stocks.CreateStock;
import UpdateStock = Stocks.UpdateStock;
import DeleteStock = Stocks.DeleteStock;
import GetJobStock = Stocks.GetJobStock;
import UpdateJobStock = Stocks.UpdateJobStock;
import UpdateJobStockLocal = Stocks.UpdateJobStockLocal;

export interface StockStateModel {
  stocks: Array<Stock>;
  currentStock: Stock;
  jobStock: JobStock;
}

@State<StockStateModel>({
  name: 'stock',
  defaults: {
    stocks: [],
    currentStock: null,
    jobStock: null,
  }
})
@Injectable()
export class StockState {
  constructor(private stockService: StockService) {
  }

  @Selector()
  static stocks(state: StockStateModel): Array<Stock> {
    return state.stocks;
  }

  @Selector()
  static stock(state: StockStateModel): Stock {
    return state.currentStock;
  }

  @Selector()
  static jobStock(state: StockStateModel): JobStock {
    return state.jobStock;
  }

  @Action(GetStocks)
  getStocks(ctx: StateContext<StockStateModel>, action: GetStocks): Observable<Stock[]> {
    if (!action.force) {
      const stocks = ctx.getState().stocks;
      if (stocks.length > 0) {
        return of(stocks);
      }
    }
    return this.stockService.getStocks().pipe(
      tap(res => {
        ctx.patchState({
          stocks: res.stocks,
        });
      }),
      map(res => res.stocks)
    );
  }

  @Action(GetStock)
  getStock(ctx: StateContext<StockStateModel>, action: GetStock): Observable<Stock> {
    if (!action.force) {
      const stock = ctx.getState().currentStock;
      if (stock && stock.id === action.id) {
        return of(stock);
      }

      const cachedStock = ctx.getState().stocks.find(p => p.id === action.id);
      if (cachedStock) {
        ctx.patchState({
          currentStock: cachedStock
        });
        return of(cachedStock);
      }
    }
    return this.stockService.getStock(action.id).pipe(
      tap(stock => {
        ctx.patchState({
          currentStock: stock
        });
      })
    );
  }

  @Action(CreateStock)
  createStock(ctx: StateContext<StockStateModel>, action: CreateStock): Observable<Stock> {
    return this.stockService.createStock(action.stock).pipe(
      tap(stock => {
        ctx.patchState({
          currentStock: stock
        });
      })
    );
  }

  @Action(UpdateStock)
  updateStock(ctx: StateContext<StockStateModel>, action: UpdateStock): Observable<Stock> {
    return this.stockService.updateStock(action.stock).pipe(
      tap(stock => {
        ctx.patchState({
          currentStock: stock
        });
      })
    );
  }

  @Action(DeleteStock)
  deleteStock(ctx: StateContext<StockStateModel>, action: DeleteStock): Observable<Stock> {
    const stocks = ctx.getState().stocks;
    const index = stocks.indexOf(action.stock);
    return this.stockService.deleteStock(action.stock.id).pipe(
      tap(stock => {
        ctx.patchState({
          currentStock: null,
          stocks: [
            ...stocks.slice(0, index),
            ...index === stocks.length - 1 ? [] : stocks.slice(index + 1)
          ]
        });
      })
    );
  }

  @Action(GetJobStock)
  getJobStock(ctx: StateContext<StockStateModel>, action: GetJobStock): Observable<JobStock> {
    return this.stockService.getJobStock(action.id).pipe(
      tap(res => {
        ctx.patchState({
          jobStock: res.jobStock
        });
      }),
      map(res => res.jobStock)
    );
  }

  @Action(UpdateJobStock)
  updateJobStock(ctx: StateContext<StockStateModel>, action: UpdateJobStock): Observable<JobStock> {
    return this.stockService.updateJobStock(action.jobStock).pipe(
      tap(res => {
        ctx.patchState({
          jobStock: res.jobStock
        });
      }),
      map(res => res.jobStock)
    );
  }

  @Action(UpdateJobStockLocal)
  updateJobStockLocal(ctx: StateContext<StockStateModel>, action: UpdateJobStockLocal): Observable<JobStock> {
    ctx.patchState({
      jobStock: action.jobStock
    });
    return of(action.jobStock);
  }

  @Action(ClearState)
  clearState(ctx: StateContext<StockStateModel>): void {
    ctx.setState({
      stocks: [],
      currentStock: null,
      jobStock: null,
    });
  }
}
